import { useFormContext } from 'react-hook-form';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { Stack, Switch } from 'src/core/ui';
import { useMarketplaceHandledByProcessor } from 'src/hooks/useMarketplaceHandledByProcessor';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  Feature,
  ListingDetails,
  ListingDetailsBroadcastSectionUpdates,
  Marketplace,
  Processor,
} from 'src/WebApiController';

import * as styles from './EnableNativeSyncSection.css';

export const EnableNativeSyncSection = ({
  disabled,
  listing,
}: {
  disabled?: boolean;
  listing: ListingDetails;
}) => {
  const { watch, setValue } =
    useFormContext<ListingDetailsBroadcastSectionUpdates>();
  const hasEnableNativeSyncFeature = useUserHasFeature(
    Feature.EnableNativeSyncToggle
  );

  const hasEnableNativeSyncForTicketVaultFeature = useUserHasFeature(
    Feature.EnableNativeSyncForTicketVault
  );

  const stubHubHandledByProcessor = useMarketplaceHandledByProcessor(
    Marketplace.StubHub
  );

  const stubHubHandledByTicketVault = useMarketplaceHandledByProcessor(
    Marketplace.StubHub,
    Processor.TicketVault
  );

  const enableNativeSync = watch('enableNativeSync');

  let shouldShow =
    hasEnableNativeSyncFeature &&
    // Only show if this listing has StubHub or Ticketmaster enabled and sync-by-processor
    listing.mkpListings?.find(
      (mkp) =>
        mkp.isEnabled &&
        mkp.mkp === Marketplace.StubHub &&
        stubHubHandledByProcessor
    ) != null;

  // if stubhub is handled by ticket vault, we should NOT show the enable native sync toggle,
  // and we don't allow change the native flag on listing
  if (hasEnableNativeSyncForTicketVaultFeature && stubHubHandledByTicketVault) {
    shouldShow = false;
  }

  if (!shouldShow) {
    return null;
  }

  return (
    <Stack gap="l" alignItems="start">
      <Switch
        checked={!!enableNativeSync}
        disabled={disabled}
        onChange={(e) => e.stopPropagation()}
        onCheckedChange={(isChecked) => {
          setValue('enableNativeSync', isChecked, {
            shouldDirty: true,
          });
        }}
      />

      <Stack direction="column">
        <span className={styles.label}>
          <Content id={ContentId.EnableNativeSyncLabel} />
        </span>
        <span className={styles.info}>
          <Content id={ContentId.EnableNativeSyncExplanation} />{' '}
          <FormatContent
            id={FormatContentId.OnlyForMarketplaces}
            params={[Marketplace.StubHub]}
          />
          .
        </span>
      </Stack>
    </Stack>
  );
};
